import React, { useState, useMemo, useCallback, useEffect } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SectionAttachImages from "./SectionAtttachImages";
import Snackbar from "@mui/material/Snackbar";
import { Alert } from "@mui/material";
import { connect } from "react-redux";
import ImageLightbox from "components/ImageLightbox/ImageLightbox";
import api from '../../api';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error";
}

const UserDetailsSection = ({user}) => {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [imageSaveFunction, setImageSaveFunction] = useState<(() => void) | null>(null);
  
  const [lightboxState, setLightboxState] = useState({
    open: false,
    imageUrl: "",
  });
  
  const [sbState, setSBState] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const openSnackbar = (
    message: string,
    severity: "success" | "info" | "warning" | "error"
  ) => {
    setSBState({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSBState((prev) => ({ ...prev, open: false }));
  };

  const handleImageChanges = useCallback((hasChanges: boolean, saveFunction?: () => void) => {
    setHasImageChanges(hasChanges);
    if (saveFunction) {
      setImageSaveFunction(() => saveFunction);
    }
  }, []);

  const handleUpdateProfile = async () => {
    setIsSubmitting(true);
    
    try {
      if (hasImageChanges && imageSaveFunction) {
        imageSaveFunction();
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      openSnackbar("Profile updated successfully!", "success");
      setHasImageChanges(false);
    } catch (error) {
      openSnackbar("Failed to update profile. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenLightbox = (imageUrl: string) => {
    setLightboxState({ open: true, imageUrl });
  };

  const handleCloseLightbox = () => {
    setLightboxState({ open: false, imageUrl: "" });
  };

  const imgSet = useMemo(() => {
    return user?.avatar ? [{
      id: user.id,
      url: user.avatar,
      caption: user.avatar_caption || ''
    }] : [];
  }, [user?.avatar, user?.avatar_caption, user?.id]);

  const avatarUrl = user?.avatar ? `${api.getUploadUrl()}/${user.avatar}` : null;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Profile Image</h2>
      <div className="w-full mt-5">
        <SectionAttachImages
          imgSet={imgSet}
          entity={'users'}
          entity_id={user?.id}
          attachmentKey="avatar"
          multiple={false}
          onSaveCallback={handleImageChanges}
          onImageClick={avatarUrl ? () => handleOpenLightbox(avatarUrl) : undefined}
        />
      </div>

      <div className="flex justify-end">
        <ButtonPrimary 
          onClick={handleUpdateProfile} 
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : hasImageChanges ? "Save Changes" : "Update Profile"}
        </ButtonPrimary>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={sbState.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={sbState.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {sbState.message}
        </Alert>
      </Snackbar>
      
      <ImageLightbox
        open={lightboxState.open}
        imageUrl={lightboxState.imageUrl}
        onClose={handleCloseLightbox}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

export default connect(mapStateToProps, { })(
  UserDetailsSection
);